import React from "react";
import SectionTitle from "../../components/layouts/others/title-section.layout";
import { Box, Container, Divider, Paper, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBullhorn,
    faCheckCircle,
    faGavel,
    faLandmark,
    faTriangleExclamation,
    faUsers,
} from "@fortawesome/free-solid-svg-icons";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    LabelList,
    Cell,
    ReferenceLine,
} from "recharts";

// @ts-ignore
import imgJpg from "../../assets/images/others/jpg-5.png";

const nationalIndexData = [
    { year: "2018", blrIndex: 100, strateIndex: 100 },
    { year: "2024", blrIndex: 126, strateIndex: 103 },
];

const panelTaxData = [
    { label: "Bois-le-Roi", value: 26, color: "#c62828" },
    { label: "5 communes du panel", value: 0, color: "#2e7d32" },
];

const financialDriftData = [
    { label: "Trésorerie", variation: -51, color: "#ef6c00" },
    { label: "Endettement", variation: 399, color: "#ad1457" },
];

const panelCountData = [
    { label: "Communes du panel", value: 9, color: "#1565c0" },
    { label: "Sans hausse fiscale", value: 5, color: "#43a047" },
];

const rappelsData = [
    { label: "Charges de fonctionnement", variation: 61, color: "#c62828" },
    { label: "Trésorerie", variation: -51, color: "#ef6c00" },
    { label: "Endettement", variation: 399, color: "#ad1457" },
    { label: "Impôts fonciers", variation: 26, color: "#1565c0" },
];

const POST20260214: React.FC = () => {
    const Bullet = ({ children }: { children: React.ReactNode }) => (
        <Box sx={{ display: "flex", gap: 1.2, alignItems: "flex-start" }}>
            <Typography sx={{ color: "#ef6c00", fontWeight: 900, mt: "2px" }}>•</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                {children}
            </Typography>
        </Box>
    );

    const StatCard = ({ value, label }: { value: string; label: string }) => (
        <Paper
            elevation={0}
            sx={{
                p: 2.2,
                borderRadius: 3,
                textAlign: "center",
                border: "1px solid rgba(198,40,40,0.25)",
                bgcolor: "#fff5f5",
            }}
        >
            <Typography variant="h4" fontWeight={900} color="#c62828">
                {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        </Paper>
    );

    return (
        <Container maxWidth="lg" sx={{ mb: 8 }}>
            <SectionTitle title="ESPACE INFORMATIONS DES HABITANTS" />

            <Box mt={3} mb={5}>
                <Typography variant="h6" fontWeight={700} align="center" gutterBottom color="primary">
                    <FontAwesomeIcon icon={faBullhorn} style={{ marginRight: 8 }} />
                    VLD2026 - Complément d'information n°2 (14&nbsp;février&nbsp;2026)
                </Typography>
                <Divider sx={{ mt: 1, mb: 3 }} />
            </Box>

            <Stack spacing={5}>
                <Paper
                    elevation={4}
                    sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: 4,
                        border: "1px solid rgba(25,118,210,0.14)",
                        background:
                            "linear-gradient(135deg, rgba(13,71,161,0.06) 0%, rgba(25,118,210,0.03) 55%, rgba(67,160,71,0.05) 100%)",
                    }}
                >
                    <Typography variant="h4" fontWeight={900} color="#0d47a1" sx={{ mb: 2 }}>
                        Complément d’information n°2 sur le tract n°3 dédié aux finances
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Nous souhaitons apporter une information complémentaire pour enrichir le débat public.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Vous avez été nombreux à nous questionner sur l’évolution des impôts locaux à Bois-le-Roi. Voici un complément
                        d’information dédié aux impôts locaux.
                    </Typography>
                </Paper>

                <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, borderLeft: "6px solid #43a047" }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#2e7d32" }} />
                        <Typography variant="h5" fontWeight={900} color="#2e7d32">
                            Synthèse
                        </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                            La pression fiscale est devenue asphyxiante pour bon nombre de nos concitoyens.
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                            Je ne peux me résoudre à accepter que la déliquescence financière provoquée par la majorité actuelle entraine
                            le départ de Bacots pour des motifs fiscaux.
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                            La pression fiscale est devenue asphyxiante pour bon nombre de nos concitoyens.
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                            Je ne peux me résoudre à accepter que la déliquescence financière provoquée par la majorité actuelle entraine
                            le départ de Bacots pour des motifs fiscaux.
                        </Typography>
                    </Stack>

                    <Grid container spacing={2} sx={{ mt: 1, justifyContent: "center" }}>
                        <Grid size={{ xs: 12, md: 8, lg: 7 }}>
                            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, border: "1px solid #e0e0e0", maxWidth: 560, mx: "auto" }}>
                                <Box
                                    component="img"
                                    src={imgJpg}
                                    alt="Jean-Philippe Guibert"
                                    sx={{
                                        width: { xs: 260, md: 320 },
                                        height: { xs: 260, md: 320 },
                                        objectFit: "cover",
                                        objectPosition: "50% 18%",
                                        borderRadius: "50%",
                                        border: "6px solid #1565c0",
                                        boxShadow: "0 12px 28px rgba(21,101,192,0.24)",
                                        display: "block",
                                        mx: "auto",
                                    }}
                                />
                                <Typography variant="caption" display="block" align="center" sx={{ mt: 1 }}>
                                    Photo JP
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Typography variant="body1" color="text.secondary" sx={{ mt: 3, lineHeight: 1.75 }}>
                        Entre 2018 et 2024, notre commune a enregistré la plus forte augmentation de la fiscalité locale parmi les
                        communes voisines. Pourtant elle a également connu la dégradation financière la plus marquée.
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.75 }}>
                        Après 8 années de mandature, le bilan financier de la majorité actuelle, en comparaison des communes voisines, est
                        sans appel :
                    </Typography>

                    <Stack spacing={1}>
                        <Bullet>La hausse la plus importante du taux communal des impôts fonciers (sur les propriétés bâties) : +26% ;</Bullet>
                        <Bullet>La plus forte augmentation de l’endettement : +399% ;</Bullet>
                        <Bullet>La deuxième plus forte baisse de sa trésorerie, après Fontainebleau : -51%.</Bullet>
                    </Stack>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <StatCard value="+26%" label="Taux communal des impôts fonciers" />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <StatCard value="+399%" label="Endettement" />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <StatCard value="-51%" label="Trésorerie" />
                        </Grid>
                    </Grid>

                    <Typography variant="body1" color="text.secondary" sx={{ mt: 3, lineHeight: 1.75 }}>
                        Un changement de cap est possible à partir de mars 2026, afin de rétablir une gestion plus rigoureuse et durable
                        des finances communales.
                    </Typography>
                </Paper>

                <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faGavel} style={{ color: "#1565c0" }} />
                        <Typography variant="h5" fontWeight={900} color="#1565c0">
                            Évolution des impôts à Bois-le-Roi
                        </Typography>
                    </Stack>

                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Éléments de définition :
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Le calcul des impôts locaux dépend de 3 facteurs :
                    </Typography>
                    <Stack spacing={1}>
                        <Bullet>Le taux communal, à la main de la majorité (révision discrétionnaire) ;</Bullet>
                        <Bullet>Les taux d’autres collectivités (révisions discrétionnaires) ;</Bullet>
                        <Bullet>Les bases fiscales, à la main de l’État (révisées chaque année à hauteur de l’inflation)</Bullet>
                    </Stack>

                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.75 }}>
                        La taxe foncière sur les propriétés bâties, ci-après TFPB, représente le principal impôt local.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Pour la première fois depuis 2008 et par 3 fois, la majorité actuelle a augmenté le taux communal de la TFBP, et
                        principalement en 2024, afin de palier la dérive financière incontrôlée.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Entre 2018 et 2024, le taux communal de la TFBP, principal impôt local, a été – très lourdement - augmenté par la
                        majorité actuelle : +26%.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Bois-le-Roi fait désormais partie des communes avec un des taux communaux de TFBP les plus élevés de France.
                    </Typography>
                </Paper>

                <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, borderLeft: "6px solid #ef6c00" }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faLandmark} style={{ color: "#ef6c00" }} />
                        <Typography variant="h5" fontWeight={900} color="#ef6c00">
                            Évolution par rapport à des communes comparables au niveau national
                        </Typography>
                    </Stack>

                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Bois-le-Roi, par la taille de sa population, est comparée à l’ensemble des communes comptant entre 5 000 à 10 000
                        habitants (nommé la « strate ») au niveau national.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.75 }}>
                        En matière d’évolution des impôts locaux, Bois-le-Roi a connu une augmentation 8 fois plus importante de la
                        fiscalité locale que sa state :
                    </Typography>
                    <Stack spacing={1}>
                        <Bullet>+26% à Bois-le-Roi ;</Bullet>
                        <Bullet>+3% pour la strate.</Bullet>
                    </Stack>

                    <Box sx={{ height: 320, mt: 2, bgcolor: "#fafafa", borderRadius: 3, p: 2, border: "1px solid #eee" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={nationalIndexData} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                <XAxis dataKey="year" />
                                <YAxis domain={[0, 140]} />
                                <Tooltip formatter={(value: any) => `${value}`} />
                                <Bar dataKey="blrIndex" name="Bois-le-Roi (indice 100 en 2018)" fill="#c62828" radius={[8, 8, 0, 0]}>
                                    <LabelList dataKey="blrIndex" position="top" />
                                </Bar>
                                <Bar dataKey="strateIndex" name="Strate (indice 100 en 2018)" fill="#2e7d32" radius={[8, 8, 0, 0]}>
                                    <LabelList dataKey="strateIndex" position="top" />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                        Lecture: base 100 en 2018. En 2024, l’indice atteint 126 à Bois-le-Roi contre 103 pour la strate.
                    </Typography>

                    <Typography variant="body1" color="text.secondary" sx={{ mt: 3, lineHeight: 1.75 }}>
                        Cette augmentation de la fiscalité locale n’a toutefois pas permis de stabiliser les finances de la commune. Pour
                        financer ses projets, la majorité actuelle a mobilisé l’ensemble des leviers financiers disponibles, avec pour
                        conséquences :
                    </Typography>
                    <Stack spacing={1}>
                        <Bullet>Baisse de 51% de la trésorerie ;</Bullet>
                        <Bullet>Hausse de 399% de l’endettement.</Bullet>
                    </Stack>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1, lineHeight: 1.75 }}>
                        Cette dégradation résulte d’une gestion insuffisamment maîtrisée des finances communales. À titre de rappel, les
                        charges de fonctionnement ont progressé à un rythme près de 4 fois supérieur à celui de l’inflation, contribuant
                        durablement au déséquilibre budgétaire.
                    </Typography>

                    <Box sx={{ height: 320, mt: 2, bgcolor: "#fafafa", borderRadius: 3, p: 2, border: "1px solid #eee" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={financialDriftData} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                <XAxis dataKey="label" />
                                <YAxis domain={[-80, 420]} unit="%" />
                                <ReferenceLine y={0} stroke="#616161" />
                                <Tooltip formatter={(value: any) => `${value}%`} />
                                <Bar dataKey="variation" radius={[8, 8, 0, 0]}>
                                    <LabelList dataKey="variation" position="top" formatter={(value: any) => `${value}%`} />
                                    {financialDriftData.map((entry, index) => (
                                        <Cell key={`drift-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                        Variations 2018-2024: baisse de la trésorerie (négatif) et hausse de l’endettement.
                    </Typography>
                </Paper>

                <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faUsers} style={{ color: "#1565c0" }} />
                        <Typography variant="h5" fontWeight={900} color="#1565c0">
                            Évolution par rapport aux communes voisines
                        </Typography>
                    </Stack>

                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Bois-le-Roi, par sa localisation, est comparée aux communes situées à proximité (« le panel »). Le panel est
                        constitué des communes suivantes : Avon, Bourron-Marlotte, Chartrettes, Fontainebleau, Héricy, Samois-sur-Seine,
                        Samoreau, Thomery et La Rochette.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.75 }}>
                        Entre 2018 et 2024, Bois-le-Roi a connu la plus forte hausse de la TFPB parmi les communes voisines.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Pourtant, l’augmentation du taux communal n’était pas une fatalité : 5 communes du panel n’ont enregistré aucune
                        hausse de leur fiscalité locale sur la même période.
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Box sx={{ height: 300, bgcolor: "#fafafa", borderRadius: 3, p: 2, border: "1px solid #eee" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={panelTaxData} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                        <XAxis dataKey="label" />
                                        <YAxis domain={[0, 30]} unit="%" />
                                        <Tooltip formatter={(value: any) => `${value}%`} />
                                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                            <LabelList dataKey="value" position="top" formatter={(value: any) => `${value}%`} />
                                            {panelTaxData.map((entry, index) => (
                                                <Cell key={`panel-tax-${index}`} fill={entry.color} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                                Taux communal TFPB 2018-2024: écart de 26 points entre Bois-le-Roi et les communes du panel sans hausse.
                            </Typography>
                        </Grid>

                        <Grid size={{ xs: 12, md: 5 }}>
                            <Box sx={{ height: 300, bgcolor: "#fafafa", borderRadius: 3, p: 2, border: "1px solid #eee" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={panelCountData} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                        <XAxis dataKey="label" />
                                        <YAxis domain={[0, 10]} />
                                        <Tooltip formatter={(value: any) => `${value} communes`} />
                                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                            <LabelList dataKey="value" position="top" />
                                            {panelCountData.map((entry, index) => (
                                                <Cell key={`panel-count-${index}`} fill={entry.color} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                                5 communes sur 9 dans le panel n’ont enregistré aucune hausse de fiscalité locale.
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                <Paper
                    elevation={2}
                    sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: 4,
                        borderLeft: "6px solid #c62828",
                        bgcolor: "#fffaf9",
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#c62828" }} />
                        <Typography variant="h5" fontWeight={900} color="#c62828">
                            Rappel Complément d’information n°1
                        </Typography>
                    </Stack>

                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                        Pour rappel (voir notre complément n°1), en 2018, Bois-le-Roi bénéficiait d’une situation financière solide,
                        largement supérieure à celle de sa strate. En 2024, Bois-le-Roi n’est désormais plus qu’une commune dont la
                        gestion financière est médiocre.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.75 }}>
                        Entre 2018 et 2024 et en comparaison des autres communes alentours, Bois-le-Roi a connu :
                    </Typography>

                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "white", border: "1px solid #eee" }}>
                            <Typography variant="subtitle1" fontWeight={900} color="#c62828" sx={{ mb: 1 }}>
                                • La plus forte augmentation des charges de fonctionnement
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                                Bois-le-Roi affiche la pire performance : ses charges de fonctionnement ont augmenté de 61%, soit une
                                progression près de 4 fois supérieure à l’inflation.
                            </Typography>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "white", border: "1px solid #eee" }}>
                            <Typography variant="subtitle1" fontWeight={900} color="#c62828" sx={{ mb: 1 }}>
                                • La deuxième plus forte baisse de sa trésorerie, après Fontainebleau
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                                Alors que sept communes ont accru leur trésorerie, Bois-le-Roi fait au contraire, partie des trois
                                communes ayant enregistré une baisse de leur trésorerie.
                            </Typography>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: "white", border: "1px solid #eee" }}>
                            <Typography variant="subtitle1" fontWeight={900} color="#c62828" sx={{ mb: 1 }}>
                                • La plus forte hausse de l’endettement
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                                En matière d’endettement, Bois-le-Roi détient un triste record : +399%, soit une progression 10 fois
                                supérieure à celle de la commune affichant la deuxième plus forte hausse.
                            </Typography>
                        </Paper>
                    </Stack>

                    <Box sx={{ height: 340, mt: 2, bgcolor: "#fafafa", borderRadius: 3, p: 2, border: "1px solid #eee" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={rappelsData} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                <XAxis dataKey="label" />
                                <YAxis domain={[-80, 420]} unit="%" />
                                <ReferenceLine y={0} stroke="#616161" />
                                <Tooltip formatter={(value: any) => `${value}%`} />
                                <Bar dataKey="variation" radius={[8, 8, 0, 0]}>
                                    <LabelList dataKey="variation" position="top" formatter={(value: any) => `${value}%`} />
                                    {rappelsData.map((entry, index) => (
                                        <Cell key={`rappel-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
                        Variations 2018-2024: la trésorerie est affichée en négatif, les autres indicateurs en hausse.
                    </Typography>
                </Paper>
            </Stack>
        </Container>
    );
};

export default POST20260214;
